import { PangkatDocument } from '~/apollo/gql/graphql'
import type { CorpsOption, Option, UnitKerjaOption } from '~/types/form'

type SelectedOption = {
  name: string
  gender: Option<string>
  angkatan: Option<number>
  pangkat: Option<number>
  corps: CorpsOption
  unit_kerja: UnitKerjaOption
  unit_organisasi: Option<string>
  jabatan: Option<string>
  unit_kerja_type: EUnitKerjaType
  jabatan_type: EJabatanType
  unit_kerja_custom: string
  jabatan_custom: string
}

export const usePersonelOptions = (payload: Ref<SelectedOption>) => {
  const loadingOptions = ref(false)

  const { options: angkatanOptions, load: loadAngkatan } = useAngkatanOptions()
  const { options: corpsOptions, load: loadCorps } = useCorpsOptions()
  const { loading: loadingUnitKerja, search: searchUnitKerja } = useSearchUnitKerja()
  const { loading: loadingSatker, search: searchSatker } = useSearchUnitKerja({ isSatker: true })
  const { loading: loadingSubsatker, search: searchSubsatker } = useSearchUnitKerja({ isSubSatker: true })
  const { options: unitOrganisasiOptions, load: loadUnitOrganisasi } = useUnitOrganisasiOptions()
  const { loading: loadingJabatan, search: searchJabatan } = useSearchJabatan()
  const { loading: loadingJabatanKepala, search: searchJabatanKepala } = useSearchJabatanKepala()
  const { result: pangkatResult, load: loadPangkat } = useLazyQuery(PangkatDocument)

  const angkatanId = computed(() => payload.value.angkatan?.value)
  const isTni = computed(() => isAngkatanTni(angkatanId.value))
  const previewFullName = computed(() => formatFullName(payload.value))
  const previewFullPosition = computed(() => formatFullPosition(payload.value))

  const pangkatOptions: ComputedRef<Option<number>[]> = computed(() => {
    if (!pangkatResult.value) return []

    return pangkatResult.value.pangkat
      .filter(pangkat => {
        if (isTni.value) return pangkat.tipe === 1
        else if (angkatanId.value === 4) return pangkat.tipe === 2 // PNS -> Gol. PNS
        else if (angkatanId.value === 5) return pangkat.tipe === 9 // CPNS -> Gol. CPNS
        else if (angkatanId.value === 8) return pangkat.tipe === 3 // PPPK -> Gol. PPPK
        else return true
      })
      .map(pangkat => ({
        label: getPangkatLabel(pangkat, angkatanId.value),
        value: pangkat.id
      }))
  })

  const loadOptions = async () => {
    loadingOptions.value = true
    await Promise.all([loadAngkatan(), loadPangkat(), loadCorps(), loadUnitOrganisasi()])
    loadingOptions.value = false
  }

  return {
    angkatanOptions,
    pangkatOptions,
    corpsOptions,
    unitOrganisasiOptions,

    isTni,
    previewFullName,
    previewFullPosition,

    loadOptions,
    searchUnitKerja,
    searchSatker,
    searchSubsatker,
    searchJabatan,
    searchJabatanKepala,

    loadingOptions,
    loadingUnitKerja,
    loadingSatker,
    loadingSubsatker,
    loadingJabatan,
    loadingJabatanKepala
  }
}
